import '@/modules/utility/ds-wms/styles/wmsTable.css';
import { useState } from 'react';
import { todayYmd } from '../../lib/dateUtils';
import { WMS_POPUP_HEADER, WMS_POPUP_ICON, WMS_POPUP_TITLE, WMS_POPUP_SUBTITLE, WMS_POPUP_CLOSE } from '../common/wmsPopupHeader';

interface EngineerProject {
  workId: string;
  workName: string;
  status: string;
  contractValue: number;
  physicalProgress: number;
  location: string;
  startDate: string;
}

interface EngineerProfileProps {
  isOpen: boolean;
  onClose: () => void;
  engineer: {
    name: string;
    designation: string;
    department: string;
    phone: string;
    email: string;
  };
  onWorkIdClick: (workId: string) => void;
}

export default function EngineerProfile({ isOpen, onClose, engineer, onWorkIdClick }: EngineerProfileProps) {
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  if (!isOpen) return null;

  // Engineer's assigned projects come from the backend API. Until wired, this is
  // empty so the profile shows "no data" / 0 (no mock data).
  const engineerProjects: EngineerProject[] = [];

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'in progress': return 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300';
      case 'completed': return 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300';
      case 'delayed': return 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300';
      case 'on hold': return 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
    }
  };

  // Calculate summary
  const totalProjects = engineerProjects.length;
  const totalValue = engineerProjects.reduce((sum, p) => sum + p.contractValue, 0);
  const inProgressCount = engineerProjects.filter(p => p.status.toLowerCase() === 'in progress').length;
  const completedCount = engineerProjects.filter(p => p.status.toLowerCase() === 'completed').length;

  // Filter projects based on selected status
  const filteredProjects = statusFilter
    ? engineerProjects.filter(p => p.status.toLowerCase() === statusFilter.toLowerCase())
    : engineerProjects;

  // Export to Excel (CSV format)
  const handleExportExcel = () => {
    const headers = [
      'S.No',
      'Work ID',
      'Work Name',
      'Location',
      'Start Date',
      'Project Value (₹)',
      'Progress (%)',
      'Status'
    ];

    const csvData = filteredProjects.map((project, index) => [
      index + 1,
      project.workId,
      project.workName,
      project.location,
      project.startDate,
      project.contractValue,
      project.physicalProgress,
      project.status
    ]);

    // Add totals row
    csvData.push([
      '',
      '',
      '',
      '',
      'TOTAL',
      filteredProjects.reduce((sum, p) => sum + p.contractValue, 0),
      '',
      ''
    ]);

    const csvContent = [
      `Engineer: ${engineer.name}`,
      `Designation: ${engineer.designation}`,
      `Department: ${engineer.department}`,
      `Phone: ${engineer.phone} | Email: ${engineer.email}`,
      '',
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `Engineer_${engineer.name.replace(/[^a-zA-Z0-9]/g, '_')}_Projects_${todayYmd()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Export to PDF
  const handleExportPDF = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Engineer Profile - ${engineer.name}</title>
        <style>
          @page { size: A4 landscape; margin: 10mm; }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: Arial, sans-serif; font-size: 10px; color: #333; padding: 15px; }
          .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #16a34a; padding-bottom: 15px; }
          .header h1 { color: #16a34a; font-size: 20px; margin-bottom: 5px; }
          .header p { color: #666; font-size: 11px; }
          .engineer-info { background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 8px; padding: 12px; margin-bottom: 15px; }
          .engineer-info h2 { color: #166534; font-size: 14px; margin-bottom: 4px; }
          .engineer-info .designation { color: #15803d; font-size: 11px; margin-bottom: 8px; }
          .info-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; font-size: 10px; }
          .info-item { display: flex; gap: 5px; }
          .info-label { color: #64748b; }
          .info-value { font-weight: 600; color: #1e293b; }
          .summary { display: flex; gap: 15px; margin-bottom: 15px; }
          .summary-item { flex: 1; background: #f1f5f9; padding: 10px; border-radius: 6px; text-align: center; }
          .summary-item .value { font-size: 16px; font-weight: 700; color: #16a34a; }
          .summary-item .label { font-size: 9px; color: #64748b; margin-top: 2px; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 15px; }
          th { background: #16a34a; color: white; padding: 8px 6px; text-align: left; font-size: 9px; font-weight: 600; border: 1px solid #15803d; }
          td { padding: 6px; border: 1px solid #e2e8f0; font-size: 9px; }
          tr:nth-child(even) { background: #f8fafc; }
          .text-right { text-align: right; }
          .text-center { text-align: center; }
          .font-semibold { font-weight: 600; }
          .status { padding: 2px 8px; border-radius: 12px; font-size: 8px; font-weight: 500; }
          .status-completed { background: #dcfce7; color: #166534; }
          .status-progress { background: #dbeafe; color: #1e40af; }
          .status-delayed { background: #fee2e2; color: #991b1b; }
          .status-hold { background: #fef9c3; color: #854d0e; }
          .progress-bar { width: 50px; height: 6px; background: #e5e7eb; border-radius: 3px; display: inline-block; vertical-align: middle; margin-right: 5px; }
          .progress-fill { height: 100%; border-radius: 3px; }
          .progress-green { background: #22c55e; }
          .progress-blue { background: #3b82f6; }
          .progress-orange { background: #f97316; }
          .totals-row { background: #dcfce7 !important; font-weight: 600; }
          .totals-row td { border-top: 2px solid #16a34a; }
          .footer { text-align: center; font-size: 8px; color: #999; margin-top: 15px; padding-top: 10px; border-top: 1px solid #e2e8f0; }
          @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>WMS - Engineer Profile</h1>
          <p>Works Management System</p>
          <p style="margin-top: 5px;">Generated on: ${new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
        </div>

        <div class="engineer-info">
          <h2>${engineer.name}</h2>
          <div class="designation">${engineer.designation}</div>
          <div class="info-grid">
            <div class="info-item"><span class="info-label">Department:</span><span class="info-value">${engineer.department}</span></div>
            <div class="info-item"><span class="info-label">Phone:</span><span class="info-value">${engineer.phone}</span></div>
            <div class="info-item"><span class="info-label">Email:</span><span class="info-value">${engineer.email}</span></div>
          </div>
        </div>

        <div class="summary">
          <div class="summary-item">
            <div class="value">${totalProjects}</div>
            <div class="label">Total Assigned</div>
          </div>
          <div class="summary-item">
            <div class="value">${completedCount}</div>
            <div class="label">Completed</div>
          </div>
          <div class="summary-item">
            <div class="value">${inProgressCount}</div>
            <div class="label">In Progress</div>
          </div>
          <div class="summary-item">
            <div class="value">${formatCurrency(totalValue)}</div>
            <div class="label">Total Project Value</div>
          </div>
        </div>

        <table data-wms-table>
          <thead>
            <tr>
              <th style="width: 35px;">S.No</th>
              <th style="width: 100px;">Work ID</th>
              <th>Work Name</th>
              <th>Location</th>
              <th class="text-right">Project Value</th>
              <th class="text-center" style="width: 80px;">Progress</th>
              <th class="text-center" style="width: 70px;">Status</th>
            </tr>
          </thead>
          <tbody>
            ${filteredProjects.map((project, index) => `
              <tr>
                <td class="text-center">${index + 1}</td>
                <td class="font-semibold" style="color: #16a34a;">${project.workId}</td>
                <td>${project.workName}</td>
                <td>${project.location}</td>
                <td class="text-right font-semibold">${formatCurrency(project.contractValue)}</td>
                <td class="text-center">
                  <div class="progress-bar">
                    <div class="progress-fill ${
                      project.physicalProgress === 100 ? 'progress-green' :
                      project.physicalProgress >= 50 ? 'progress-blue' : 'progress-orange'
                    }" style="width: ${project.physicalProgress}%"></div>
                  </div>
                  ${project.physicalProgress}%
                </td>
                <td class="text-center">
                  <span class="status ${
                    project.status === 'Completed' ? 'status-completed' :
                    project.status === 'In Progress' ? 'status-progress' :
                    project.status === 'Delayed' ? 'status-delayed' : 'status-hold'
                  }">${project.status}</span>
                </td>
              </tr>
            `).join('')}
            <tr class="totals-row">
              <td colspan="4" class="text-right font-semibold">TOTAL</td>
              <td class="text-right font-semibold">${formatCurrency(filteredProjects.reduce((sum, p) => sum + p.contractValue, 0))}</td>
              <td colspan="2"></td>
            </tr>
          </tbody>
        </table>

        <div class="footer">
          <p>WMS - Works Management System</p>
          <p>This is a computer-generated document. No signature required.</p>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  return (
    <div className={`fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] ${isFullscreen ? 'p-0' : 'p-4'}`}>
      <div className={`bg-gray-100 dark:bg-gray-900 shadow-2xl overflow-hidden flex flex-col ${isFullscreen ? 'w-full h-full rounded-none' : 'rounded-xl w-full max-w-5xl max-h-[90vh]'}`}>
        {/* Header */}
        <div className={WMS_POPUP_HEADER}>
            <div className="flex items-center gap-2 min-w-0">
              <div className={WMS_POPUP_ICON}>
                <i className="ri-user-star-fill"></i>
              </div>
              <div className="min-w-0">
                <h2 className={WMS_POPUP_TITLE}>{engineer.name}</h2>
                <p className={WMS_POPUP_SUBTITLE}>{engineer.designation}</p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <button
                onClick={() => setIsFullscreen(!isFullscreen)}
                className={WMS_POPUP_CLOSE}
                title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
              >
                <i className={`${isFullscreen ? 'ri-fullscreen-exit-line' : 'ri-fullscreen-line'} text-base`}></i>
              </button>
              <button
                onClick={handleExportPDF}
                className={WMS_POPUP_CLOSE}
                title="Export PDF"
              >
                <i className="ri-file-pdf-line text-base"></i>
              </button>
              <button
                onClick={onClose}
                className={WMS_POPUP_CLOSE}
                title="Close"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
        </div>

        {/* Engineer Info Row */}
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex-shrink-0">
          <div className="flex flex-wrap gap-6 text-sm">
            <div className="flex items-center gap-2">
              <i className="ri-building-line text-gray-400 dark:text-gray-500"></i>
              <span className="text-gray-600 dark:text-gray-400">Department:</span>
              <span className="font-medium text-gray-800 dark:text-gray-200">{engineer.department}</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-phone-line text-gray-400 dark:text-gray-500"></i>
              <a href={`tel:${engineer.phone}`} className="font-medium text-blue-600 dark:text-blue-400 hover:underline">{engineer.phone}</a>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-mail-line text-gray-400 dark:text-gray-500"></i>
              <a href={`mailto:${engineer.email}`} className="font-medium text-blue-600 dark:text-blue-400 hover:underline">{engineer.email}</a>
            </div>
          </div>
        </div>

        {/* Summary Cards - Clickable for filtering */}
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0 bg-white dark:bg-gray-800">
          <div className="grid grid-cols-4 gap-3">
            <button
              onClick={() => setStatusFilter(null)}
              className={`rounded-lg p-3 border text-center transition-all cursor-pointer ${
                statusFilter === null
                  ? 'bg-blue-100 dark:bg-blue-900/40 border-blue-400 dark:border-blue-600 ring-2 ring-blue-400 dark:ring-blue-600 ring-offset-1 dark:ring-offset-gray-800'
                  : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/40 hover:border-blue-300'
              }`}
            >
              <p className="text-2xl font-bold text-blue-700 dark:text-blue-400">{totalProjects}</p>
              <span className="text-xs text-blue-600 dark:text-blue-400">Total Assigned</span>
            </button>
            <button
              onClick={() => setStatusFilter(statusFilter === 'completed' ? null : 'completed')}
              className={`rounded-lg p-3 border text-center transition-all cursor-pointer ${
                statusFilter === 'completed'
                  ? 'bg-green-100 dark:bg-green-900/40 border-green-400 dark:border-green-600 ring-2 ring-green-400 dark:ring-green-600 ring-offset-1 dark:ring-offset-gray-800'
                  : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 hover:bg-green-100 dark:hover:bg-green-900/40 hover:border-green-300'
              }`}
            >
              <p className="text-2xl font-bold text-green-700 dark:text-green-400">{completedCount}</p>
              <span className="text-xs text-green-600 dark:text-green-400">Completed</span>
            </button>
            <button
              onClick={() => setStatusFilter(statusFilter === 'in progress' ? null : 'in progress')}
              className={`rounded-lg p-3 border text-center transition-all cursor-pointer ${
                statusFilter === 'in progress'
                  ? 'bg-orange-100 dark:bg-orange-900/40 border-orange-400 dark:border-orange-600 ring-2 ring-orange-400 dark:ring-orange-600 ring-offset-1 dark:ring-offset-gray-800'
                  : 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800 hover:bg-orange-100 dark:hover:bg-orange-900/40 hover:border-orange-300'
              }`}
            >
              <p className="text-2xl font-bold text-orange-700 dark:text-orange-400">{inProgressCount}</p>
              <span className="text-xs text-orange-600 dark:text-orange-400">In Progress</span>
            </button>
            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3 border border-purple-200 dark:border-purple-800 text-center">
              <p className="text-lg font-bold text-purple-700 dark:text-purple-400">{formatCurrency(totalValue)}</p>
              <span className="text-xs text-purple-600 dark:text-purple-400">Total Project Value</span>
            </div>
          </div>
          {statusFilter && (
            <div className="mt-2 flex items-center gap-2 text-sm">
              <span className="text-gray-500 dark:text-gray-400">Filtering by:</span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(statusFilter)}`}>
                {statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}
              </span>
              <button
                onClick={() => setStatusFilter(null)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 ml-1"
                title="Clear filter"
              >
                <i className="ri-close-circle-line"></i>
              </button>
            </div>
          )}
        </div>

        {/* Projects Table */}
        <div className="flex-1 overflow-auto p-4">
          <h4 className="text-lg font-semibold text-gray-900 mb-3">
            Assigned Works
            {statusFilter && <span className="text-sm font-normal text-gray-500 ml-2">({filteredProjects.length} of {totalProjects})</span>}
          </h4>
          <div className="overflow-x-auto border border-orange-200 rounded-lg shadow-sm shadow-orange-100/50 ring-1 ring-orange-100">
            <table data-wms-table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b-2 border-gray-400">
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase border-r border-gray-300">S.No</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase border-r border-gray-300">Work ID</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase border-r border-gray-300">Work Name</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase border-r border-gray-300">Location</th>
                  <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase border-r border-gray-300">Project Value</th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase border-r border-gray-300">Progress</th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredProjects.map((project, index) => (
                  <tr key={project.workId} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} border-b border-gray-200 hover:bg-green-50 transition-colors`}>
                    <td className="px-3 py-2.5 text-sm text-gray-900 border-r border-gray-200">{index + 1}</td>
                    <td className="px-3 py-2.5 text-sm border-r border-gray-200">
                      <button
                        onClick={() => onWorkIdClick(project.workId)}
                        className="text-blue-600 hover:text-blue-800 hover:underline font-semibold"
                      >
                        {project.workId}
                      </button>
                    </td>
                    <td className="px-3 py-2.5 text-sm text-gray-900 border-r border-gray-200 max-w-xs">
                      <span className="line-clamp-2">{project.workName}</span>
                    </td>
                    <td className="px-3 py-2.5 text-sm text-gray-600 border-r border-gray-200">
                      <div className="flex items-center gap-1">
                        <i className="ri-map-pin-2-fill text-gray-400 text-xs"></i>
                        {project.location}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-sm font-semibold text-gray-900 text-right border-r border-gray-200">
                      {formatCurrency(project.contractValue)}
                    </td>
                    <td className="px-3 py-2.5 text-center border-r border-gray-200">
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${
                              project.physicalProgress === 100
                                ? 'bg-green-500'
                                : project.physicalProgress >= 50
                                ? 'bg-blue-500'
                                : 'bg-orange-500'
                            }`}
                            style={{ width: `${project.physicalProgress}%` }}
                          />
                        </div>
                        <span className="text-xs font-medium text-gray-600">{project.physicalProgress}%</span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}>
                        {project.status}
                      </span>
                    </td>
                  </tr>
                ))}
                {filteredProjects.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                      <i className="ri-folder-open-line text-3xl mb-2"></i>
                      <p>No projects found with the selected filter</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 border-t border-gray-200 px-4 py-3 flex justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            <button
              onClick={handleExportExcel}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 hover:border-green-300 transition-all"
              title="Export to Excel"
            >
              <i className="ri-file-excel-2-line text-lg"></i>
              <span>Excel</span>
            </button>
            <button
              onClick={handleExportPDF}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 hover:border-red-300 transition-all"
              title="Export to PDF"
            >
              <i className="ri-file-pdf-line text-lg"></i>
              <span>PDF</span>
            </button>
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
